import React, { useMemo } from 'react'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material'
import McPageHelpers from '../../pages/mc-components/McPageHelpers'
import { getCountryName } from '../../pages/mc-components/market-overview/MarketOverviewTable/MarketOverviewTable.settings'
import { useSDSsearchParams } from '../../store/searchDataStore'
import { useTranslation } from 'react-i18next'
import { useGlobalStoreData } from '../../store/globalStore'
import { MONTH_KEYS } from '../../utils/Constants'

const palette = {
  headerBg: '#e7eef7',
  headerText: '#1f3a6d',
  border: '#d6def2',
  rowHover: '#f5f8ff',
  stripe: '#fbfcff',
  stickyBg: '#fff',
  worldBody: '#eef3ff',
  partnerBody: '#fff'
}

const dividerSX = { borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: palette.headerText }

const HsTradeTimeMatrix = ({ data, title }) => {
  const { t } = useTranslation()
  const searchParams = useSDSsearchParams()
  const countriesGSD = useGlobalStoreData.countries()

  const timeTags = data?.timeTags ?? []
  const rows = data?.rows ?? []
  const isYearly = useMemo(() => timeTags.every(t => Number(t) < 10000), [timeTags])

  const parsedMonthly = useMemo(() => {
    if (isYearly) return []
    return timeTags.map(ti => {
      const year = Math.floor(ti / 100)
      const month = ti % 100
      const monthKey = MONTH_KEYS[month - 1]
      return { year, month, labelMonth: t(`months.${monthKey}`) }
    })
  }, [timeTags, isYearly])

  const yearGroups = useMemo(() => {
    if (isYearly) return []
    const groups = []
    for (const p of parsedMonthly) {
      const last = groups[groups.length - 1]
      if (!last || last.year !== p.year) groups.push({ year: p.year, count: 1 })
      else last.count += 1
    }
    return groups
  }, [parsedMonthly, isYearly])

  const splitIdx = useMemo(() => {
    if (isYearly) return []
    const idx = []
    for (let i = 1; i < parsedMonthly.length; i++) {
      if (parsedMonthly[i].year !== parsedMonthly[i - 1].year) idx.push(i)
    }
    return idx
  }, [parsedMonthly, isYearly])

  const tableRows = useMemo(() => rows.map(r => ({
    hsCode: r.hsCode,
    hsName: r.hsName,
    worldImport: timeTags.map((_, i) => r.importWorldValues?.[i] ?? 0),
    worldExport: timeTags.map((_, i) => r.exportWorldValues?.[i] ?? 0),
    partnerImport: timeTags.map((_, i) => r.importPartnerValues?.[i] ?? 0),
    partnerExport: timeTags.map((_, i) => r.exportPartnerValues?.[i] ?? 0)
  })), [rows, timeTags])

  return (
    <Box>
      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${palette.border}`, boxShadow: '0 2px 10px rgba(22,34,66,.06)' }}>
        <Table size="small" sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          'th, td': { borderColor: palette.border },
          '& thead th': { fontWeight: 700, color: palette.headerText, whiteSpace: 'nowrap', bgcolor: palette.headerBg },
          '& tbody td': { whiteSpace: 'nowrap' }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, minWidth: 160, bgcolor: palette.headerBg, borderRight: `1px solid ${palette.border}`, fontWeight: 800 }}>
                {title ?? t('columns.hsCode')} ({t('thousand_dollar')})
              </TableCell>
              <TableCell align="center" colSpan={timeTags.length * 2} sx={{ fontWeight: 800 }}>{t('world')}</TableCell>
              <TableCell align="center" colSpan={timeTags.length * 2} sx={{ fontWeight: 800, ...dividerSX }}>{getCountryName(searchParams.yourCountry, countriesGSD, 'country_code')}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: palette.headerBg, borderRight: `1px solid ${palette.border}` }}/>
              <TableCell align="center" colSpan={timeTags.length} sx={{ fontWeight: 800 }}>{t('worldTabs.import')}</TableCell>
              <TableCell align="center" colSpan={timeTags.length} sx={{ fontWeight: 800 }}>{t('worldTabs.export')}</TableCell>
              <TableCell align="center" colSpan={timeTags.length} sx={{ fontWeight: 800, ...dividerSX }}>{t('worldTabs.import')}</TableCell>
              <TableCell align="center" colSpan={timeTags.length} sx={{ fontWeight: 800 }}>{t('worldTabs.export')}</TableCell>
            </TableRow>

            {isYearly
              ? (
                <TableRow>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: palette.headerBg, borderRight: `1px solid ${palette.border}` }}/>
                  {timeTags.map((y, i) => <TableCell key={`wi-y-${y}-${i}`} align="right" sx={{ fontWeight: 800 }}>{y}</TableCell>)}
                  {timeTags.map((y, i) => <TableCell key={`we-y-${y}-${i}`} align="right" sx={{ fontWeight: 800 }}>{y}</TableCell>)}
                  {timeTags.map((y, i) => <TableCell key={`pi-y-${y}-${i}`} align="right" sx={{ fontWeight: 800, ...(i === 0 ? dividerSX : {}) }}>{y}</TableCell>)}
                  {timeTags.map((y, i) => <TableCell key={`pe-y-${y}-${i}`} align="right" sx={{ fontWeight: 800 }}>{y}</TableCell>)}
                </TableRow>
                )
              : (
                <>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: palette.headerBg, borderRight: `1px solid ${palette.border}` }}/>
                    {yearGroups.map((g, i) => <TableCell key={`wi-yg-${g.year}-${i}`} align="center" colSpan={g.count} sx={{ fontWeight: 800, ...(i > 0 ? dividerSX : {}) }}>{g.year}</TableCell>)}
                    {yearGroups.map((g, i) => <TableCell key={`we-yg-${g.year}-${i}`} align="center" colSpan={g.count} sx={{ fontWeight: 800 }}>{g.year}</TableCell>)}
                    {yearGroups.map((g, i) => <TableCell key={`pi-yg-${g.year}-${i}`} align="center" colSpan={g.count} sx={{ fontWeight: 800, ...dividerSX }}>{g.year}</TableCell>)}
                    {yearGroups.map((g, i) => <TableCell key={`pe-yg-${g.year}-${i}`} align="center" colSpan={g.count} sx={{ fontWeight: 800 }}>{g.year}</TableCell>)}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: palette.headerBg, borderRight: `1px solid ${palette.border}` }}/>
                    {parsedMonthly.map(
                      (p, i) => <TableCell key={`wi-m-${p.year}-${p.month}`} align="right" sx={{ fontWeight: 700, ...(splitIdx.includes(i) ? dividerSX : {}) }}>{p.labelMonth}</TableCell>)}
                    {parsedMonthly.map(
                      (p, i) => <TableCell key={`we-m-${p.year}-${p.month}`} align="right" sx={{ fontWeight: 700, ...(splitIdx.includes(i) ? dividerSX : {}) }}>{p.labelMonth}</TableCell>)}
                    {parsedMonthly.map((p, i) => <TableCell key={`pi-m-${p.year}-${p.month}`} align="right" sx={{ fontWeight: 700, ...(i === 0 ? dividerSX : {}) }}>{p.labelMonth}</TableCell>)}
                    {parsedMonthly.map((p, i) => <TableCell key={`pe-m-${p.year}-${p.month}`} align="right" sx={{ fontWeight: 700 }}>{p.labelMonth}</TableCell>)}
                  </TableRow>
                </>
                )}
          </TableHead>

          <TableBody>
            {tableRows.map((r, rowIdx) => (
              <TableRow key={r.hsCode} hover sx={{ bgcolor: rowIdx % 2 ? palette.stripe : '#fff', '&:hover': { bgcolor: palette.rowHover } }}>
                <TableCell
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: palette.stickyBg,
                    borderRight: `1px solid ${palette.border}`,
                    fontWeight: 600
                  }}
                >
                  {r.hsCode}
                  <Tooltip title={r.hsName} arrow placement="top">
                    <span
                      style={{
                        fontSize: '0.6rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        cursor: 'help'
                      }}
                    >
                      {r.hsName}
                    </span>
                  </Tooltip>
                </TableCell>

                {r.worldImport.map((v, i) => (
                  <TableCell key={`wi-${r.hsCode}-${i}`} align="right" sx={{
                    fontVariantNumeric: 'tabular-nums',
                    bgcolor: palette.worldBody,
                    ...(splitIdx.includes(i) ? dividerSX : {}),
                    '&:hover': { bgcolor: '#dbe7ff', transition: 'background .2s' }
                  }}>
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}
                {r.worldExport.map((v, i) => (
                  <TableCell key={`we-${r.hsCode}-${i}`} align="right" sx={{
                    fontVariantNumeric: 'tabular-nums',
                    bgcolor: palette.worldBody,
                    ...(splitIdx.includes(i) ? dividerSX : {}),
                    '&:hover': { bgcolor: '#dbe7ff', transition: 'background .2s' }
                  }}>
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}

                {r.partnerImport.map((v, i) => (
                  <TableCell key={`pi-${r.hsCode}-${i}`} align="right"
                             sx={{ fontVariantNumeric: 'tabular-nums', bgcolor: palette.partnerBody, ...(i === 0 ? dividerSX : {}), '&:hover': { bgcolor: '#f9f9f9', transition: 'background .2s' } }}>
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}
                {r.partnerExport.map((v, i) => (
                  <TableCell key={`pe-${r.hsCode}-${i}`} align="right"
                             sx={{ fontVariantNumeric: 'tabular-nums', bgcolor: palette.partnerBody, '&:hover': { bgcolor: '#f9f9f9', transition: 'background .2s' } }}>
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default HsTradeTimeMatrix
